
import { useState, useEffect, ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  responsive?: boolean;
  priority?: boolean;
  title?: string;
}

export function OptimizedImage({ 
  src, 
  alt, 
  className, 
  width, 
  height, 
  responsive = true,
  priority = false,
  title,
  ...props 
}: ImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  useEffect(() => {
    setIsLoaded(false);
    setError(false);
    setCurrentSrc(src);
  }, [src]);

  const handleLoad = () => setIsLoaded(true);
  
  const handleError = () => {
    // If WebP fails, try original format
    if (currentSrc.includes('.webp') && !src.includes('.webp')) {
      setCurrentSrc(src);
    } else {
      setError(true);
    }
  };

  // Generate responsive sizes
  const generateSizes = () => {
    if (!responsive || !width) return undefined;
    
    return [
      `(max-width: 640px) ${Math.min(width, 320)}px`,
      `(max-width: 768px) ${Math.min(width, 384)}px`, 
      `(max-width: 1024px) ${Math.min(width, 512)}px`,
      `${width}px`
    ].join(', ');
  };

  // Generate srcSet for Supabase storage URLs
  const generateSrcSet = () => {
    if (!responsive || !width || !height) return undefined;
    
    if (currentSrc.includes('supabase.co/storage')) {
      const baseUrl = currentSrc.split('?')[0];
      return [
        `${baseUrl}?width=${Math.round(width * 0.5)}&height=${Math.round(height * 0.5)}&resize=contain&quality=85 0.5x`,
        `${baseUrl}?width=${width}&height=${height}&resize=contain&quality=85 1x`,
        `${baseUrl}?width=${Math.round(width * 1.5)}&height=${Math.round(height * 1.5)}&resize=contain&quality=85 1.5x`,
        `${baseUrl}?width=${Math.round(width * 2)}&height=${Math.round(height * 2)}&resize=contain&quality=85 2x`
      ].join(', ');
    }
    
    return undefined;
  };

  return (
    <img
      src={currentSrc}
      srcSet={generateSrcSet()}
      sizes={generateSizes()}
      alt={alt}
      title={title || alt}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding={priority ? "sync" : "async"}
      width={width}
      height={height}
      onLoad={handleLoad}
      onError={handleError}
      className={cn(
        "object-cover",
        priority ? "" : "transition-opacity duration-300",
        priority ? "" : (isLoaded ? "opacity-100" : "opacity-0"),
        error ? "opacity-50" : "",
        className
      )}
      {...props}
    />
  );
}
